import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import querystring from "query-string";
import { api } from "@/src/core/api/client";
import type { IListingSearchRespose } from "@/src/shared/types/listing";
import type {
  IListingSearchParams,
  ILocationSearchResponse,
  ILocationSearchResult,
} from "@/src/shared/features/search/types";

const searchKeys = {
  all: ["search"] as const,
  locations: (term: string) => [...searchKeys.all, "locations", term] as const,
};

/** Raw calls. Throw an ApiError on failure. */
export const searchApi = {
  /** Search service listing search; the location landing pages call it at build time. */
  async getListings(
    query: IListingSearchParams,
  ): Promise<IListingSearchRespose> {
    const { data } = await api.search.get<IListingSearchRespose>(
      `/listings?${querystring.stringify(query)}`,
    );
    return data;
  },

  /** Typeahead: popular areas and recent searches for a blank `q`, else matches and hotels. */
  async searchLocations(q: string): Promise<ILocationSearchResult> {
    const { data } = await api.python.get<ILocationSearchResponse>(
      "/public/locations/search",
      { params: { q } },
    );
    return data.data;
  },

  /** "Clear": hides this visitor's recent searches. */
  async clearRecentSearches(): Promise<void> {
    await api.python.delete("/public/locations/search/recent");
  },
};

/** The results with the term they answer, which differs while placeholder data shows. */
interface ILocationSearchData {
  term: string;
  result: ILocationSearchResult;
}

/**
 * The location box's results for the trimmed term. The previous results stay on
 * screen (dimmed by the caller) while the next ones load, as the app does.
 */
export const useLocationSearchQuery = (term: string) => {
  const q = term.trim();
  return useQuery({
    queryKey: searchKeys.locations(q),
    queryFn: async (): Promise<ILocationSearchData> => ({
      term: q,
      result: await searchApi.searchLocations(q),
    }),
    placeholderData: keepPreviousData,
    // The blank list carries this visitor's recent searches, which every search adds
    // to: reload it each time the box opens, as the app does when its screen shows.
    ...(q === "" && { staleTime: 0, refetchOnMount: "always" as const }),
  });
};

/** Empties the recent searches at once and puts them back if the call fails. */
export const useClearRecentSearchesMutation = () => {
  const queryClient = useQueryClient();
  const key = searchKeys.locations("");
  return useMutation({
    mutationFn: searchApi.clearRecentSearches,
    onMutate: () => {
      const previous = queryClient.getQueryData<ILocationSearchData>(key);
      if (previous) {
        queryClient.setQueryData<ILocationSearchData>(key, {
          ...previous,
          result: { ...previous.result, recent_searches: [] },
        });
      }
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
  });
};
